import React from 'react';
import { Drawer, Box, Typography, Tabs, Tab, Button } from 'minimal-ui';
import { Snackbar, Alert, Chip } from '@mui/material';
import DrawerHeader from './DrawerHeader';
import PromotionInfo from './PromotionInfo';
import SearchBar from './SearchBar';
import ProductList from './ProductList';
import ConfirmSwapDialog from './ConfirmSwapDialog';
import DiscardChangesDialog from './DiscardChangesDialog';
import LoadingSkeleton from './LoadingSkeleton';
import { ConfirmDialog } from '../../../common/confirm-dialog';
import { usePromotionDrawer } from '../hooks/usePromotionDrawer';
import SalesStockLimitDialog from '../../common/SalesStockLimitDialog';

export default function PromotionDetailsDrawer({ open, onClose, promotion, selectedCustomer: selectedCustomerProp, platform: platformProp }) {
    const {
        activeTab,
        searchTerm,
        productQuantities,
        productPricingTypes,
        existingCartQuantities,
        snackbar,
        swapData,
        showConfirmDialog,
        isSubmittingSwap,
        showDiscardDialog,
        promotionDetails,
        isLoading,
        error,
        hasGetProducts,
        selectedBuyProductsCount,
        selectedGetProductsCount,
        hasOtherPromotionsInCart,
        cartLineItemsForLimits,
        filteredBuyProducts,
        filteredOfferProducts,
        buyProductGroupIds,
        selectedBuyGroupId,
        setSelectedBuyGroupId,
        getProductGroupIds,
        selectedGetGroupId,
        setSelectedGetGroupId,
        selectedCustomer,
        setSearchTerm,
        handleTabChange,
        handleQuantityChange,
        notifyQuantityLimitExceeded,
        handlePricingTypeChange,
        handleAddToCart,
        handleSubmitSwap,
        handleCloseDrawer,
        handleDiscardChanges,
        setShowConfirmDialog,
        setShowDiscardDialog,
        showInvalidProductsDialog,
        setShowInvalidProductsDialog,
        showSwapErrorDialog,
        setShowSwapErrorDialog,
        swapErrorMessage,
        setSnackbar,
        limitDialog,
        closeLimitDialog,
        showCompulsoryDialog,
        setShowCompulsoryDialog,
        compulsoryDialogCodes,
        handleCloseCompulsoryDialog
    } = usePromotionDrawer(open, promotion, onClose, { selectedCustomer: selectedCustomerProp, platform: platformProp });

    const handleCloseSnackbar = () => {
        setSnackbar(prev => ({ ...prev, open: false }));
    };

    const handleConfirmOfferProducts = () => {
        if (swapData.removed.length === 0 && swapData.added.length === 0) {
            setSnackbar({
                open: true,
                message: 'No changes to confirm',
                severity: 'warning'
            });
            return;
        }
        setShowConfirmDialog(true);
    };

    if (isLoading) {
        return (
            <Drawer
                open={open}
                onClose={handleCloseDrawer}
                anchor="right"
                PaperProps={{
                    sx: {
                        width: 550,
                        minWidth: 550,
                        maxWidth: 550,
                        display: 'flex',
                        flexDirection: 'column',
                        height: '100vh',
                        zIndex: 1400,
                        overflow: 'hidden'
                    }
                }}
            >
                <LoadingSkeleton />
            </Drawer>
        );
    }

    if (error) {
        return (
            <Drawer
                open={open}
                onClose={handleCloseDrawer}
                anchor="right"
                PaperProps={{
                    sx: {
                        width: 550,
                        minWidth: 550,
                        maxWidth: 550,
                        display: 'flex',
                        flexDirection: 'column',
                        height: '100vh',
                        zIndex: 1400,
                        overflow: 'hidden'
                    }
                }}
            >
                <Box sx={{
                    display: 'flex',
                    justifyContent: 'center',
                    alignItems: 'center',
                    height: '100%',
                    backgroundColor: 'white'
                }}>
                    <Typography color="error">Error loading promotion details. Please try again.</Typography>
                </Box>
            </Drawer>
        );
    }

    return (
        <Drawer
            open={open}
            onClose={handleCloseDrawer}
            anchor="right"
            PaperProps={{
                sx: {
                    width: 550,
                    minWidth: 550,
                    maxWidth: 550,
                    display: 'flex',
                    flexDirection: 'column',
                    height: '100vh',
                    zIndex: 1400,
                    overflow: 'hidden'
                }
            }}
        >
            <Box sx={{
                display: 'flex',
                flexDirection: 'column',
                height: '100%',
                width: '100%',
                maxWidth: '100%',
                backgroundColor: 'white',
                overflow: 'hidden'
            }}>
                <DrawerHeader onClose={handleCloseDrawer} />

                <PromotionInfo promotionDetails={promotionDetails} />

                <Box sx={{
                    px: 2,
                    flexShrink: 0,
                    display: 'flex',
                    justifyContent: 'space-between',
                    alignItems: 'center',
                    width: '100%',
                    minWidth: 0,
                    maxWidth: '100%'
                }}>
                    <Tabs
                        value={activeTab}
                        onChange={handleTabChange}
                        sx={{
                            flex: 1,
                            minWidth: 0,
                            maxWidth: '100%',
                            width: '100%',
                            '& .MuiTab-root': {
                                textTransform: 'none',
                                fontWeight: 500,
                                fontSize: '16px',
                                minHeight: '40px'
                            },
                            '& .Mui-selected': {
                                color: '#E27172 !important'
                            },
                            '& .MuiTabs-indicator': {
                                backgroundColor: '#E27172',
                                height: '2px'
                            }
                        }}
                    >
                        <Tab label="Buy Products" />
                        {hasGetProducts && <Tab label="Offer Products" sx={{color: '#212121'}} />}
                    </Tabs>
                </Box>

                {activeTab === 0 && buyProductGroupIds.length > 1 && (
                    <Box
                        sx={{
                            px: 2,
                            py: 1,
                            mt: 1,
                            flexShrink: 0,
                            width: '100%',
                            maxWidth: '100%',
                            minWidth: 0,
                            borderTop: '1px solid #E0E0E0',
                            borderBottom: '1px solid #E0E0E0',
                            backgroundColor: '#fafafa',
                            display: 'flex',
                            flexWrap: 'nowrap',
                            gap: 1,
                            overflowX: 'auto',
                            WebkitOverflowScrolling: 'touch',
                            '&::-webkit-scrollbar': { height: 6 },
                            '&::-webkit-scrollbar-thumb': {
                                borderRadius: 3,
                                backgroundColor: 'rgba(0,0,0,0.2)',
                            },
                        }}
                    >
                        {buyProductGroupIds.map((gid) => {
                            const idStr = String(gid);
                            const selected = idStr === String(selectedBuyGroupId);
                            return (
                                <Chip
                                    key={idStr}
                                    onClick={() => setSelectedBuyGroupId(idStr)}
                                    label={`Group ${gid}`}
                                    size="small"
                                    variant="soft"
                                    color={selected ? 'warning' : 'default'}
                                    sx={{
                                        flexShrink: 0,
                                        cursor: 'pointer',
                                        fontWeight: selected ? 700 : 500,
                                        fontSize: { xs: '10px', sm: '11px' },
                                        height: { xs: '26px', sm: '28px' },
                                        borderRadius: '999px',
                                        ...(selected
                                            ? {
                                                  border: '1px solid',
                                                  borderColor: 'warning.main',
                                                  backgroundColor: 'warning.lighter',
                                                  color: 'warning.darker',
                                              }
                                            : {
                                                  border: '1px solid',
                                                  borderColor: '#E0E0E0',
                                                  backgroundColor: 'common.white',
                                                  color: 'text.primary',
                                                  '&:hover': {
                                                      borderColor: 'warning.light',
                                                      backgroundColor: 'warning.lighter',
                                                  },
                                              }),
                                    }}
                                />
                            );
                        })}
                    </Box>
                )}

                {hasGetProducts && activeTab === 1 && getProductGroupIds.length > 1 && (
                    <Box
                        sx={{
                            px: 2,
                            py: 1,
                            mt: 1,
                            flexShrink: 0,
                            width: '100%',
                            maxWidth: '100%',
                            minWidth: 0,
                            borderTop: '1px solid #E0E0E0',
                            borderBottom: '1px solid #E0E0E0',
                            backgroundColor: '#fafafa',
                            display: 'flex',
                            flexWrap: 'nowrap',
                            gap: 1,
                            overflowX: 'auto',
                            WebkitOverflowScrolling: 'touch',
                            '&::-webkit-scrollbar': { height: 6 },
                            '&::-webkit-scrollbar-thumb': {
                                borderRadius: 3,
                                backgroundColor: 'rgba(0,0,0,0.2)',
                            },
                        }}
                    >
                        {getProductGroupIds.map((gid) => {
                            const idStr = String(gid);
                            const selected = idStr === String(selectedGetGroupId);
                            return (
                                <Chip
                                    key={`get-${idStr}`}
                                    onClick={() => setSelectedGetGroupId(idStr)}
                                    label={`Group ${gid}`}
                                    size="small"
                                    variant="soft"
                                    color={selected ? 'warning' : 'default'}
                                    sx={{
                                        flexShrink: 0,
                                        cursor: 'pointer',
                                        fontWeight: selected ? 700 : 500,
                                        fontSize: { xs: '10px', sm: '11px' },
                                        height: { xs: '26px', sm: '28px' },
                                        borderRadius: '999px',
                                        ...(selected
                                            ? {
                                                  border: '1px solid',
                                                  borderColor: 'warning.main',
                                                  backgroundColor: 'warning.lighter',
                                                  color: 'warning.darker',
                                              }
                                            : {
                                                  border: '1px solid',
                                                  borderColor: '#E0E0E0',
                                                  backgroundColor: 'common.white',
                                                  color: 'text.primary',
                                                  '&:hover': {
                                                      borderColor: 'warning.light',
                                                      backgroundColor: 'warning.lighter',
                                                  },
                                              }),
                                    }}
                                />
                            );
                        })}
                    </Box>
                )}

                <SearchBar searchTerm={searchTerm} onSearchChange={setSearchTerm} />

                <Box sx={{ px: 2, flexShrink: 0 }}>
                    <Typography variant="body2" fontWeight={500}>
                        {activeTab === 0 ? selectedBuyProductsCount : selectedGetProductsCount} Product Selected
                    </Typography>
                </Box>

                <Box sx={{
                    flex: 1,
                    overflowY: 'auto',
                    overflowX: 'hidden',
                    p: 2,
                    pt: 1,
                    width: '100%',
                    maxWidth: '100%',
                    minWidth: 0,
                    position: 'relative'
                }}>
                    {activeTab === 0 && (
                        <ProductList
                            products={filteredBuyProducts}
                            tabType="buy"
                            productQuantities={productQuantities}
                            existingCartQuantities={existingCartQuantities}
                            productPricingTypes={productPricingTypes}
                            cartItems={cartLineItemsForLimits}
                            promotionDetails={promotionDetails}
                            onQuantityChange={handleQuantityChange}
                            onQuantityLimitExceeded={notifyQuantityLimitExceeded}
                            onPricingTypeChange={handlePricingTypeChange}
                            onAddToCart={handleAddToCart}
                            hasOtherPromotionsInCart={false}
                        />
                    )}

                    {hasGetProducts && activeTab === 1 && (
                        <ProductList
                            products={filteredOfferProducts}
                            tabType="get"
                            productQuantities={productQuantities}
                            existingCartQuantities={existingCartQuantities}
                            productPricingTypes={productPricingTypes}
                            cartItems={cartLineItemsForLimits}
                            promotionDetails={promotionDetails}
                            onQuantityChange={handleQuantityChange}
                            onQuantityLimitExceeded={notifyQuantityLimitExceeded}
                            onPricingTypeChange={handlePricingTypeChange}
                            onAddToCart={handleAddToCart}
                            hasOtherPromotionsInCart={hasOtherPromotionsInCart}
                        />
                    )}
                </Box>

                {hasGetProducts && activeTab === 1 && (
                    <Box sx={{
                        px: 2,
                        pb: 2,
                        pt: 1,
                        flexShrink: 0,
                        display: 'flex',
                        justifyContent: 'flex-end',
                        width: '100%'
                    }}>
                        <Button
                            variant="contained"
                            onClick={handleConfirmOfferProducts}
                            disabled={isSubmittingSwap}
                            sx={{
                                '&.MuiButton-root': {
                                    backgroundColor: '#1C252E',
                                    color: 'white',
                                },
                                '&.Mui-disabled': {
                                    backgroundColor: '#E0E0E0',
                                    color: '#9E9E9E'
                                }
                            }}
                        >
                            Confirm
                        </Button>
                    </Box>
                )}
            </Box>

            <SalesStockLimitDialog
                open={limitDialog.open}
                onClose={closeLimitDialog}
                productName={limitDialog.productName}
                productCode={limitDialog.productCode}
                maxQty={limitDialog.maxQty}
                message={limitDialog.message || ''}
                plainTitle={limitDialog.plainTitle}
            />

            <Snackbar
                open={Boolean(snackbar.open && !snackbar.useErrorDialog)}
                autoHideDuration={4000}
                onClose={handleCloseSnackbar}
                anchorOrigin={{ vertical: 'top', horizontal: 'right' }}
                sx={{ zIndex: 2000 }}
            >
                <Alert
                    onClose={handleCloseSnackbar}
                    severity={snackbar.severity}
                    variant="filled"
                    sx={{ width: '100%' }}
                >
                    {snackbar.message}
                </Alert>
            </Snackbar>

            <ConfirmSwapDialog
                open={showConfirmDialog}
                onClose={() => setShowConfirmDialog(false)}
                onConfirm={handleSubmitSwap}
                swapData={swapData}
                isSubmitting={isSubmittingSwap}
            />

            <DiscardChangesDialog
                open={showDiscardDialog}
                onClose={() => setShowDiscardDialog(false)}
                onDiscard={handleDiscardChanges}
            />

            <ConfirmDialog
                open={showCompulsoryDialog}
                onClose={handleCloseCompulsoryDialog}
                title="Compulsory product"
                content={
                    <Typography>
                        <Box component="span" sx={{ fontWeight: 700 }}>
                            {compulsoryDialogCodes.join(', ')}
                        </Box>
                        {' '}
                        product is compulsory
                    </Typography>
                }
                action={
                    <Button
                        variant="contained"
                        color="error"
                        onClick={handleCloseCompulsoryDialog}
                        sx={{
                            '&.MuiButton-root': {
                                backgroundColor: '#E27172',
                                color: 'white',
                            }
                        }}
                    >
                        OK
                    </Button>
                }
            />

            <ConfirmDialog
                open={showInvalidProductsDialog}
                onClose={() => setShowInvalidProductsDialog(false)}
                title="Not Valid Products"
                content={
                    <Typography>
                        Not Valid products in cart
                    </Typography>
                }
                action={
                    <Button
                        variant="contained"
                        color="error"
                        onClick={() => setShowInvalidProductsDialog(false)}
                        sx={{
                            '&.MuiButton-root': {
                                backgroundColor: '#E27172',
                                color: 'white',
                            }
                        }}
                    >
                        OK
                    </Button>
                }
            />

            <ConfirmDialog
                open={showSwapErrorDialog}
                onClose={() => setShowSwapErrorDialog(false)}
                title="Promotion Not Applied"
                content={
                    <Typography>
                        {swapErrorMessage}
                    </Typography>
                }
                action={
                    <Button
                        variant="contained"
                        color="error"
                        onClick={() => setShowSwapErrorDialog(false)}
                        sx={{
                            '&.MuiButton-root': {
                                backgroundColor: '#E27172',
                                color: 'white',
                            }
                        }}
                    >
                        OK
                    </Button>
                }
            />
        </Drawer>
    );
}
